/* eslint-disable complexity */
import { IntlShape } from 'react-intl';
import get from 'lodash/get';

import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { ReportTotal } from '@futrli-sdk/predict';
import { Consolidation } from '@futrli-sdk/consolidations';
import { NodeStaticId } from '@futrli/predict/forecast/prediction-builder/types';

import {
  CASH_FLOW_MAIN_CATEGORIES,
  CATEGORY_NAME_INDEXES,
  PeriodType,
  REPORT_SECTION,
  SIZES
} from '../../../../core/constants';
import {
  ConsolidatedForecastReport,
  ConsolidatedForecastReportStructure,
  ConsolidatedNodeInfo,
  ConsolidatedNodeStructure,
  ConsolidatedReportData,
  NodeInfo
} from '../../../../core/types';
import { RowStyleOptions, RowType, RowsType, TotalRow } from '../../reports/data-grid/types';
import { ROW_KIND } from '../../reports/data-grid/constants';
import { UserGridSettings } from '../../reports/user-grid-settings/types';

const initRowStyleOptions: Required<RowStyleOptions> = {
  forceRowSize: null,
  hidePeriodToDateDifference: false,
  disableLargeSize: false,
  isBold: false,
  isFirst: false,
  isLast: false,
  isMainHeadline: null,
  padding: false,
  percentage: false,
  startingLevel: 0
};

/**
 * Returns All node refs from the tree
 */
const collectRefs = (nodes: ConsolidatedNodeStructure[] | null): string[] => {
  if (!nodes) {
    return [];
  }
  const refs: string[] = [];
  for (const node of nodes) {
    refs.push(node.ref, ...collectRefs(node.children));
  }
  return refs;
};

/**
 * @returns Node from consolidated forecast report for given ref
 */
export const findNodeByRef = (
  nodes: ConsolidatedNodeStructure[] | undefined,
  targetRef: string
): ConsolidatedNodeStructure | undefined => {
  for (const node of nodes ?? []) {
    if (node.ref === targetRef) {
      return node;
    }
    const children = node.children ?? [];
    const found = findNodeByRef(children, targetRef);
    if (found) {
      return found;
    }
  }
  return undefined;
};

/**
 * @returns Report section for given ref
 */
export const getRefReportSection = (
  structure: ConsolidatedForecastReportStructure,
  refToFind: string
): REPORT_SECTION | null => {
  function searchChildren(children: ConsolidatedNodeStructure[], ref: string): boolean {
    for (const child of children) {
      if (child.ref === ref) {
        return true;
      }
      if (child.children && searchChildren(child.children, ref)) {
        return true;
      }
    }
    return false;
  }

  for (const section of Object.keys(structure) as REPORT_SECTION[]) {
    const found = structure[section].some(item => searchChildren([item], refToFind));
    if (found) {
      return section;
    }
  }

  return null;
};

/**
 * Returns ref of node that is needed to node for consolidation's specific category
 * @param node_info Node info part of response.
 * @param category Category for displaying data
 * @returns Ref of consolidation specific part of tree for given category
 */
export const getConsolidationRef = (
  consolidatedForecastReport: ConsolidatedForecastReport,
  category: string,
  reportSection: REPORT_SECTION
): string | null => {
  const { node_info } = consolidatedForecastReport;
  const refs = Object.keys(node_info.by_ref);
  for (const ref of refs) {
    if (isTopLevelStaticRowType(node_info.by_ref[ref].type) && node_info.by_ref[ref].static_id === category) {
      // We can have 2 entries which will have consolidation-static type and matched category so we
      // need to check which report section contains ref and compare it with the report section from function args
      const refReportSection = getRefReportSection(consolidatedForecastReport.structure, ref);
      if (refReportSection === reportSection) {
        return ref;
      }
    }
  }

  return null;
};

const getSystemAssumptionRowName = (intl: IntlShape, node: NodeInfo) => {
  if (node?.system_assumption_type === 'vat-payments') {
    return intl.formatMessage({ id: 'predict.vat_gst_payment', defaultMessage: 'VAT / GST payment' });
  }
  if (node.system_assumption_type === 'year-end') {
    return intl.formatMessage({ id: 'predict.year_end', defaultMessage: 'Year end transfer' });
  }
  if (node.system_assumption_type === 'unpaid-invoices') {
    if (get(node, 'extra_display_params.invoices_or_bills') === 'invoices') {
      return intl.formatMessage({ id: 'predict.unpaid_invoices', defaultMessage: 'Unpaid invoices' });
    }
    if (get(node, 'extra_display_params.invoices_or_bills') === 'bills') {
      return intl.formatMessage({ id: 'predict.unpaid_bills', defaultMessage: 'Unpaid bills' });
    }
  }

  return node.name;
};

/**
 * @returns Rows for display in prediction pages data grids
 */
export const getConsolidatedGridRows = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  workspace: WorkspacesListingItem,
  consolidation: Consolidation,
  nodesTree: ConsolidatedNodeStructure[],
  nodes: ConsolidatedNodeStructure[],
  data: ConsolidatedReportData,
  node_info: ConsolidatedNodeInfo,
  reportSection: REPORT_SECTION,
  expandedRows: UserGridSettings['expandedRows'],
  category: CATEGORY_NAME_INDEXES,
  isTotalsPage: boolean,
  showZeroValueAccounts = false,
  level = 0,
  directParentRef: string | null = null,
  parentRefs: string[] = []
): RowsType => {
  const isNotIgnoredNode = (n: ConsolidatedNodeStructure) =>
    (!showZeroValueAccounts && !data.by_ref[n.ref].is_unused) || showZeroValueAccounts;

  let flatArray: RowsType = [];
  nodes?.forEach((node, index) => {
    const rowType = node_info.by_ref[node.ref].type;
    const staticId = node_info.by_ref[node.ref].static_id;
    const children = node.children ?? [];

    let orgShortCode;
    let shouldShowShortCode = false;

    const coaStructureNeedsAccountShortcodes = [
      'flatten',
      'group_by_identical_names',
      'group_by_identical_custom_names'
    ].includes(consolidation.account_structure);

    // if any of ancestors is not in expanded rows array that means it is collapsed and account/group shouldn't be displayed
    if ((rowType !== 'organisation-static' && !isTotalsPage) || (!isTopLevelStaticRowType(rowType) && isTotalsPage)) {
      const hideEntry = parentRefs.some(p => expandedRows.indexOf(p) < 0);
      if (hideEntry) {
        return;
      }
    }

    // Specific case where we need to display node of type consolidation-static inside of data grid.
    if (
      staticId === 'current_year_earnings' &&
      isTopLevelStaticRowType(rowType) &&
      category === CATEGORY_NAME_INDEXES.equity
    ) {
      const hideEntry = parentRefs.some(p => expandedRows.indexOf(p) < 0);
      if (hideEntry) {
        return;
      }
      const name = staticId ? nodeStaticIds[staticId as NodeStaticId] : node_info.by_ref[node.ref].name;
      flatArray.push({
        section: reportSection,
        isFirst: false,
        isLast: false,
        kind: ROW_KIND.organisation,
        childCount: children.filter(isNotIgnoredNode).length,
        padding: false,
        level,
        isBold: true,
        account: {
          index: node.ref,
          name,
          type: ROW_KIND.group,
          canonical_category: category,
          period_to_date: data.by_ref[node.ref]?.period_to_date || '',
          period_data: data.by_ref[node.ref]?.row
        }
      });
    }

    if (rowType === 'account' || rowType === 'eliminated-account') {
      const parent = directParentRef ? findNodeByRef(nodesTree, directParentRef) : undefined;

      if (isNotIgnoredNode(node)) {
        const accountIndex = parent?.children?.findIndex(c => c.ref === node.ref);

        if (rowType === 'eliminated-account' || (rowType === 'account' && coaStructureNeedsAccountShortcodes)) {
          const { organisation_id } = node_info.by_ref[node.ref];
          orgShortCode = consolidation.organisations.find(o => o.organisation_id === organisation_id)?.short_code;
        }

        if (orgShortCode && (rowType === 'eliminated-account' || coaStructureNeedsAccountShortcodes)) {
          shouldShowShortCode = true;
        }

        flatArray.push({
          isFirst: accountIndex === 0,
          isLast: Boolean(accountIndex && parent?.children && accountIndex === parent?.children?.length - 1),
          section: reportSection,
          padding: false,
          level,
          kind: ROW_KIND.account,
          childCount: children.filter(isNotIgnoredNode).length,
          account: {
            index: node.ref,
            name: `${node_info.by_ref[node.ref]?.name}${shouldShowShortCode ? ` - ${orgShortCode}` : ''}`,
            type: ROW_KIND.account,
            canonical_category: category,
            period_to_date: data.by_ref[node.ref]?.period_to_date || '',
            period_data: data.by_ref[node.ref]?.row,
            unused_account: false,
            average_days_to_pay: 0,
            system_account_role: node_info.by_ref[node.ref]?.system_account_role || null
          }
        });
      }
    } else if (rowType === 'group' || rowType === 'group-by-account-name' || rowType === 'group-by-org-group-name') {
      flatArray.push({
        section: reportSection,
        isFirst: false, //TODO: calculate group isFirst
        isLast: false, //TODO: calculate group isLast
        kind: ROW_KIND.group,
        childCount: children.filter(isNotIgnoredNode).length,
        padding: false,
        level,
        isBold: true,
        account: {
          index: node.ref,
          name: node_info.by_ref[node.ref]?.name,
          type: ROW_KIND.group,
          canonical_category: category,
          period_to_date: data.by_ref[node.ref]?.period_to_date || '',
          period_data: data.by_ref[node.ref]?.row
        }
      });
    } else if (rowType === 'organisation-static' || rowType === 'elimination-group') {
      const organisationId = node_info.by_ref[node.ref]?.organisation_id;
      const hasNameOverride = rowType === 'organisation-static' && node_info.by_ref[node.ref].static_id === category;

      if (rowType === 'organisation-static' && coaStructureNeedsAccountShortcodes) {
        const { organisation_id } = node_info.by_ref[node.ref];
        orgShortCode = consolidation.organisations.find(o => o.organisation_id === organisation_id)?.short_code;
        shouldShowShortCode = true;
      }

      const name = hasNameOverride
        ? workspace.organisations.find(o => o.id === organisationId)?.name
        : rowType === 'organisation-static' && staticId
        ? nodeStaticIds[staticId as NodeStaticId]
        : node_info.by_ref[node.ref].name;

      flatArray.push({
        section: reportSection,
        isFirst: false, //TODO: calculate group isFirst
        isLast: false, //TODO: calculate group isLast
        kind: ROW_KIND.organisation,
        childCount: children.filter(isNotIgnoredNode).length,
        padding: false,
        level,
        isBold: true,
        account: {
          index: node.ref,
          name: `${name}${shouldShowShortCode ? ` - ${orgShortCode}` : ''}`,
          type: ROW_KIND.group,
          canonical_category: category,
          period_to_date: data.by_ref[node.ref]?.period_to_date || '',
          period_data: data.by_ref[node.ref]?.row
        }
      });
    } else if (rowType === 'assumption' || rowType === 'system-assumption') {
      if (
        (directParentRef && !showZeroValueAccounts && !data.by_ref[directParentRef].is_unused) ||
        showZeroValueAccounts
      ) {
        if ((node_info.by_ref[node.ref].assumption_type as string) === 'SheetsAssumption') {
          flatArray.push({
            section: reportSection,
            kind: ROW_KIND.sheetsAssumption,
            padding: false,
            level,
            parentName: directParentRef ? node_info.by_ref[directParentRef].name : '',
            siblingCount: nodes.length,
            siblingIndex: index,
            account: {
              parent_row_id: directParentRef || '',
              index: node_info.by_ref[node.ref]?.assumption_id || '',
              name: node_info.by_ref[node.ref]?.name,
              type: 'SheetsAssumption',
              canonical_category: category,
              period_data: data.by_ref[node.ref]?.row,
              is_derived: rowType === 'system-assumption' ? true : !!node_info.by_ref[node.ref]?.is_derived
            }
          });
        } else {
          let name = '';
          if (rowType === 'system-assumption') {
            name = getSystemAssumptionRowName(intl, node_info.by_ref[node.ref]);
          } else {
            name = node_info.by_ref[node.ref]?.name;
          }
          flatArray.push({
            section: reportSection,
            kind: ROW_KIND.assumption,
            padding: false,
            level,
            parentName: directParentRef ? node_info.by_ref[directParentRef].name : '',
            siblingCount: nodes.length,
            siblingIndex: index,
            account: {
              parent_row_id: directParentRef || '',
              index: node_info.by_ref[node.ref]?.assumption_id || '',
              name,
              type: node_info.by_ref[node.ref].assumption_type!,
              canonical_category: category,
              period_data: data.by_ref[node.ref]?.row,
              excluded_from_report: !!node_info.by_ref[node.ref]?.excluded_from_report,
              is_derived: rowType === 'system-assumption' ? true : !!node_info.by_ref[node.ref]?.is_derived,
              paymenttype_id: '',
              paymenttype_name: '',
              paymenttype_type: '',
              imported_from_advisor: null,
              is_excel_assumption: null,
              primary_account_name: node_info.by_ref[node.ref]?.primary_account_name || null
            },
            isSystemAssumption: rowType === 'system-assumption'
          });
        }
      }
    }

    // Don't remember consolidation as parent of next node because consolidation row is not in data grid for profit and loss tabs,
    // remember consolidation as parent for totals pages (Profit & Loss, Balance Sheet and Cash Flow Statement)
    const newParentRefs =
      isTopLevelStaticRowType(rowType) && !isTotalsPage ? [...parentRefs] : [...parentRefs, node.ref];
    flatArray = flatArray.concat(
      getConsolidatedGridRows(
        intl,
        nodeStaticIds,
        workspace,
        consolidation,
        nodesTree,
        children,
        data,
        node_info,
        reportSection,
        expandedRows,
        category,
        isTotalsPage,
        showZeroValueAccounts,
        level + 1,
        node.ref,
        newParentRefs
      )
    );
  });
  return flatArray;
};

/**
 * @returns Accounts rows with groups and orgs, total rows for each category and total cash predicted data (for graph above totals)
 */
export const getConsolidatedGridInfo = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  reportSection: REPORT_SECTION,
  category: CATEGORY_NAME_INDEXES,
  workspace: Workspace | WorkspacesListingItem | null,
  expandedRows: (string | number)[],
  periodType?: PeriodType,
  consolidatedForecastReport?: ConsolidatedForecastReport | null,
  consolidation?: Consolidation,
  showZeroValueAccounts?: boolean
) => {
  if (!consolidatedForecastReport || !workspace || !periodType) {
    return { dataGridRows: null, totalRow: null };
  }
  // Find consolidation node ref for given category
  const consolidationCategoryRef = getConsolidationRef(consolidatedForecastReport, category, reportSection);
  const consolidationBankAccountsRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.bank_accounts,
    REPORT_SECTION.BalanceSheet
  );
  if (!consolidationCategoryRef || !consolidationBankAccountsRef || !consolidation) {
    return {
      dataGridRows: null,
      totalRow: null,
      totalCashPredictRow: null
    };
  }

  const consolidatedCategoryNode = findNodeByRef(
    consolidatedForecastReport.structure[reportSection],
    consolidationCategoryRef
  );

  if (!consolidatedCategoryNode) {
    return { dataGridRows: null, totalRow: null, totalCashPredictRow: null };
  }

  const totalRow = getConsolidatedCategoryTotal(
    intl,
    nodeStaticIds,
    consolidationCategoryRef,
    consolidatedForecastReport,
    category,
    reportSection
  );

  const dataArray = getConsolidatedGridRows(
    intl,
    nodeStaticIds,
    workspace as WorkspacesListingItem,
    consolidation,
    [consolidatedCategoryNode],
    [consolidatedCategoryNode],
    consolidatedForecastReport.data,
    consolidatedForecastReport.node_info,
    reportSection,
    expandedRows,
    category,
    false,
    showZeroValueAccounts
  );

  return { dataGridRows: dataArray, totalRow };
};

/**
 * Overrides `income` and `cost_of_sales` category total name
 */
const getConsolidatedTotalNameWithIncomeOverride = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  category: CATEGORY_NAME_INDEXES,
  nodeType: string,
  totalRowName: string,
  staticId?: NodeStaticId
) => {
  // special case for Income account total on the home screen only
  if (category === CATEGORY_NAME_INDEXES.income) {
    return intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' });
  }
  if (category === CATEGORY_NAME_INDEXES.cost_of_sales) {
    return intl.formatMessage({ id: 'predict.predictions.costs', defaultMessage: 'Costs' });
  }

  if ((nodeType === 'consolidation-static' || nodeType === 'organisation-static') && typeof staticId !== 'undefined') {
    return nodeStaticIds[staticId as NodeStaticId];
  }

  return totalRowName || '';
};

/**
 *
 * @returns Consolidation total row for given category
 */
export const getConsolidatedCategoryTotal = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  totalNodeRef: string,
  consolidatedForecastReport: ConsolidatedForecastReport,
  category: CATEGORY_NAME_INDEXES,
  reportSection: REPORT_SECTION,
  options: RowStyleOptions = {},
  isTotalPage?: boolean
): TotalRow => {
  const { startingLevel, padding, isFirst, isLast, forceRowSize, hidePeriodToDateDifference } = {
    ...initRowStyleOptions,
    ...options
  };
  const { data, node_info, structure } = consolidatedForecastReport;
  const totalRowValues = data.by_ref[totalNodeRef].row;
  const staticId = node_info.by_ref[totalNodeRef].static_id;
  const node = findNodeByRef(structure[reportSection], totalNodeRef);
  const nodeType = node_info.by_ref[totalNodeRef].type;
  const totalRowName =
    nodeType === 'consolidation-static' || nodeType === 'organisation-static'
      ? nodeStaticIds[staticId as NodeStaticId]
      : node_info.by_ref[totalNodeRef].name;

  return {
    section: reportSection,
    isBold: true,
    kind: ROW_KIND.total,
    name: isTotalPage
      ? totalRowName
      : getConsolidatedTotalNameWithIncomeOverride(
          intl,
          nodeStaticIds,
          category,
          nodeType,
          totalRowName,
          staticId as NodeStaticId
        ),
    subtitle: undefined,
    account: {
      canonical_category: category,
      index: totalNodeRef,
      name: isTotalPage
        ? totalRowName
        : getConsolidatedTotalNameWithIncomeOverride(
            intl,
            nodeStaticIds,
            category,
            nodeType,
            totalRowName,
            staticId as NodeStaticId
          ),
      period_data: totalRowValues,
      type: ROW_KIND.total,
      period_to_date: data.by_ref[totalNodeRef]?.period_to_date || '',
      static_id: node_info.by_ref[totalNodeRef].static_id as ReportTotal['static_id']
    },
    disableLargeSize: false,
    padding,
    childCount: isTotalPage ? node?.children?.length || 0 : 0,
    level: startingLevel,
    isFirst,
    isLast,
    forceRowSize,
    hidePeriodToDateDifference
  };
};

/**
 * @returns Calculation data grid row
 */
export const getConsolidatedCalculationRow = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  consolidatedForecastReport: ConsolidatedForecastReport,
  reportSection: REPORT_SECTION,
  category: string,
  options: RowStyleOptions = {},
  searchJoinedAccount = false
) => {
  const consolidationCategoryRef = getConsolidationRef(
    consolidatedForecastReport,
    category as CATEGORY_NAME_INDEXES,
    reportSection
  );
  if (!consolidationCategoryRef) {
    return null;
  }

  const categoryTotalRow = getConsolidatedCategoryTotal(
    intl,
    nodeStaticIds,
    consolidationCategoryRef,
    consolidatedForecastReport,
    category as CATEGORY_NAME_INDEXES,
    reportSection,
    {},
    true
  );

  let categoryJoinedTotalRow = null;
  if (searchJoinedAccount) {
    const consolidationCategoryJoinedRef = getConsolidationRef(
      consolidatedForecastReport,
      `${category}_%` as CATEGORY_NAME_INDEXES,
      reportSection
    );
    if (consolidationCategoryJoinedRef) {
      categoryJoinedTotalRow = getConsolidatedCategoryTotal(
        intl,
        nodeStaticIds,
        consolidationCategoryJoinedRef,
        consolidatedForecastReport,
        `${category}_%` as CATEGORY_NAME_INDEXES,
        reportSection,
        {},
        true
      );
    }
  }

  const { startingLevel, padding, percentage, isFirst, isLast } = { ...initRowStyleOptions, ...options };

  return categoryTotalRow
    ? [
        {
          ...categoryTotalRow,
          childCount: 0,
          hidePeriodToDateDifference: true,
          isBold: true,
          forceRowSize: SIZES.MEDIUM,
          isFirst,
          isLast,
          percentage,
          padding,
          kind: ROW_KIND.calculation,
          joinedRow: categoryJoinedTotalRow ? { ...categoryJoinedTotalRow, percentage: true } : undefined,
          level: startingLevel
        }
      ]
    : [];
};

/**
 * @returns Category total + accounts/groups under category total.
 */
export const getConsolidatedCategoryTotalWithAccounts = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  workspace: WorkspacesListingItem,
  consolidation: Consolidation,
  expandedRows: (string | number)[],
  ref: string | null,
  consolidatedForecastReport: ConsolidatedForecastReport,
  category: CATEGORY_NAME_INDEXES,
  reportSection: REPORT_SECTION,
  node?: ConsolidatedNodeStructure | null,
  showZeroValueAccounts = false,
  options: RowStyleOptions = {}
): RowType[] => {
  if (!ref || !node) {
    return [];
  }
  const categoryTotal = getConsolidatedCategoryTotal(
    intl,
    nodeStaticIds,
    ref,
    consolidatedForecastReport,
    category,
    reportSection,
    options,
    true
  );
  const gridRows = getConsolidatedGridRows(
    intl,
    nodeStaticIds,
    workspace,
    consolidation,
    consolidatedForecastReport.structure[reportSection],
    [node],
    consolidatedForecastReport.data,
    consolidatedForecastReport.node_info,
    reportSection,
    expandedRows,
    category,
    true,
    showZeroValueAccounts,
    options.startingLevel || 0
  );

  return [categoryTotal, ...gridRows];
};

/**
 * @returns Data structure for Profit and loss tab
 */
export const getProfitAndLossConsolidatedAccountCategories = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  workspace: WorkspacesListingItem | null,
  reportSection: REPORT_SECTION,
  expandedRows: (string | number)[],
  consolidatedForecastReport?: ConsolidatedForecastReport | null,
  consolidation?: Consolidation,
  isCashFlowStatementPage = false,
  showZeroValueAccounts = false
): RowsType | null => {
  if (!workspace || !consolidatedForecastReport || !consolidation) {
    return null;
  }

  // Income
  const consolidationIncomeRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.income,
    reportSection
  );
  const incomeNode = consolidationIncomeRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], consolidationIncomeRef)
    : null;

  // Cost of sales
  const consolidationCostOfSalesRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.cost_of_sales,
    reportSection
  );
  const costOfSalesNode = consolidationCostOfSalesRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], consolidationCostOfSalesRef)
    : null;

  // Expenses
  const consolidationExpensesRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.expenses,
    reportSection
  );
  const expensesNode = consolidationExpensesRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], consolidationExpensesRef)
    : null;

  // Other expenses
  const consolidationOtherExpensesRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.other_expenses,
    reportSection
  );
  const otherExpensesNode = consolidationOtherExpensesRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], consolidationOtherExpensesRef)
    : null;

  // Other income
  const consolidationOtherIncomeRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.other_income,
    reportSection
  );
  const otherIncomeNode = consolidationOtherIncomeRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], consolidationOtherIncomeRef)
    : null;

  const otherExpensesNodeAccounts = otherExpensesNode
    ? getConsolidatedGridRows(
        intl,
        nodeStaticIds,
        workspace,
        consolidation,
        consolidatedForecastReport.structure[reportSection],
        [otherExpensesNode],
        consolidatedForecastReport.data,
        consolidatedForecastReport.node_info,
        reportSection,
        [...expandedRows, consolidationOtherExpensesRef || ''],
        CATEGORY_NAME_INDEXES.other_expenses,
        true,
        showZeroValueAccounts
      )
    : [];
  const otherIncomeNodeAccounts = otherIncomeNode
    ? getConsolidatedGridRows(
        intl,
        nodeStaticIds,
        workspace,
        consolidation,
        consolidatedForecastReport.structure[reportSection],
        [otherIncomeNode],
        consolidatedForecastReport.data,
        consolidatedForecastReport.node_info,
        reportSection,
        [...expandedRows, consolidationOtherIncomeRef || ''],
        CATEGORY_NAME_INDEXES.other_income,
        true,
        showZeroValueAccounts
      )
    : [];

  const showOperationalProfit = Boolean(otherIncomeNodeAccounts.length || otherExpensesNodeAccounts.length);

  const operatingProfitCalculationRow =
    showOperationalProfit && !isCashFlowStatementPage
      ? (getConsolidatedCalculationRow(
          intl,
          nodeStaticIds,
          consolidatedForecastReport,
          reportSection,
          'operating_profit',
          {},
          true
        ) as RowsType) || []
      : [];

  return [
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      consolidationIncomeRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.income,
      reportSection,
      incomeNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : { isFirst: true }
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      consolidationCostOfSalesRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.cost_of_sales,
      reportSection,
      costOfSalesNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    ),
    ...(isCashFlowStatementPage
      ? []
      : (getConsolidatedCalculationRow(
          intl,
          nodeStaticIds,
          consolidatedForecastReport,
          reportSection,
          'gross_profit',
          { isFirst: true, isLast: true },
          true
        ) as RowsType) || []),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      consolidationExpensesRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.expenses,
      reportSection,
      expensesNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : { padding: true }
    ),
    ...operatingProfitCalculationRow,
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      consolidationOtherIncomeRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.other_income,
      reportSection,
      otherIncomeNode,
      showZeroValueAccounts,
      isCashFlowStatementPage
        ? { startingLevel: 1 }
        : {
            padding: true
          }
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      consolidationOtherExpensesRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.other_expenses,
      reportSection,
      otherExpensesNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : { isLast: true }
    ),
    ...(isCashFlowStatementPage
      ? []
      : (getConsolidatedCalculationRow(
          intl,
          nodeStaticIds,
          consolidatedForecastReport,
          reportSection,
          'net_profit',
          { isFirst: true, isLast: true },
          true
        ) as RowsType) || [])
  ];
};

/**
 * @returns Data structure for Balance sheet tab
 */
export const getBalanceSheetConsolidatedAccountCategories = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  workspace: WorkspacesListingItem | null,
  reportSection: REPORT_SECTION,
  expandedRows: (string | number)[],
  consolidatedForecastReport?: ConsolidatedForecastReport | null,
  consolidation?: Consolidation,
  isCashFlowStatementPage = false,
  showZeroValueAccounts = false
): RowsType | null => {
  if (!workspace || !consolidatedForecastReport || !consolidation) {
    return null;
  }

  // Bank accounts
  const bankAccountsRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.bank_accounts,
    reportSection
  );
  const bankAccountsNode = bankAccountsRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], bankAccountsRef)
    : null;

  // Fixed assets
  const fixedAssetsRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.fixed_assets,
    reportSection
  );
  const fixedAssetsNode = fixedAssetsRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], fixedAssetsRef)
    : null;

  // Current assets
  const currentAssetsRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.current_assets,
    reportSection
  );
  const currentAssetsNode = currentAssetsRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], currentAssetsRef)
    : null;

  // Non current assets
  const nonCurrentAssetsRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.non_current_assets,
    reportSection
  );
  const nonCurrentAssetsNode = nonCurrentAssetsRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], nonCurrentAssetsRef)
    : null;

  // Current liabilities
  const currentLiabilitiesRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.current_liabilities,
    reportSection
  );
  const currentLiabilitiesNode = currentLiabilitiesRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], currentLiabilitiesRef)
    : null;

  // Non current liabilities
  const nonCurrentLiabilitiesRef = getConsolidationRef(
    consolidatedForecastReport,
    CATEGORY_NAME_INDEXES.non_current_liabilities,
    reportSection
  );
  const nonCurrentLiabilitiesNode = nonCurrentLiabilitiesRef
    ? findNodeByRef(consolidatedForecastReport.structure[reportSection], nonCurrentLiabilitiesRef)
    : null;

  // Equity
  const equityRef = getConsolidationRef(consolidatedForecastReport, CATEGORY_NAME_INDEXES.equity, reportSection);
  const equityNode = equityRef ? findNodeByRef(consolidatedForecastReport.structure[reportSection], equityRef) : null;

  return [
    ...(isCashFlowStatementPage
      ? []
      : getConsolidatedCategoryTotalWithAccounts(
          intl,
          nodeStaticIds,
          workspace,
          consolidation,
          expandedRows,
          bankAccountsRef,
          consolidatedForecastReport,
          CATEGORY_NAME_INDEXES.bank_accounts,
          reportSection,
          bankAccountsNode,
          showZeroValueAccounts,
          { isFirst: true }
        )),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      fixedAssetsRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.fixed_assets,
      reportSection,
      fixedAssetsNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      currentAssetsRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.current_assets,
      reportSection,
      currentAssetsNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      nonCurrentAssetsRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.non_current_assets,
      reportSection,
      nonCurrentAssetsNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    ),
    ...(isCashFlowStatementPage
      ? []
      : (getConsolidatedCalculationRow(
          intl,
          nodeStaticIds,
          consolidatedForecastReport,
          reportSection,
          'total_assets'
        ) as RowsType) || []),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      currentLiabilitiesRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.current_liabilities,
      reportSection,
      currentLiabilitiesNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : { padding: true }
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      nonCurrentLiabilitiesRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.non_current_liabilities,
      reportSection,
      nonCurrentLiabilitiesNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    ),
    ...(isCashFlowStatementPage
      ? []
      : (getConsolidatedCalculationRow(
          intl,
          nodeStaticIds,
          consolidatedForecastReport,
          reportSection,
          'total_liabilities'
        ) as RowsType) || []),
    ...(isCashFlowStatementPage
      ? []
      : (getConsolidatedCalculationRow(intl, nodeStaticIds, consolidatedForecastReport, reportSection, 'net_assets', {
          padding: true
        }) as RowsType) || []),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      equityRef,
      consolidatedForecastReport,
      CATEGORY_NAME_INDEXES.equity,
      reportSection,
      equityNode,
      showZeroValueAccounts,
      isCashFlowStatementPage ? { startingLevel: 1 } : undefined
    )
  ];
};

/**
 *
 * @returns Totals for cash flow statement tab
 */
const getConsolidationCashFlowTotals = (
  nodeStaticIds: Record<NodeStaticId, string>,
  totalNodeRef: string,
  consolidatedForecastReport: ConsolidatedForecastReport,
  reportCategory: string,
  options: RowStyleOptions = {}
) => {
  const { padding, isFirst, isLast, forceRowSize } = { ...initRowStyleOptions, ...options };
  const { data, node_info, structure } = consolidatedForecastReport;
  const totalRowValues = data.by_ref[totalNodeRef].row;

  const staticId = node_info.by_ref[totalNodeRef].static_id;
  const node = findNodeByRef(structure[REPORT_SECTION.CashFlow], totalNodeRef);
  const nodeType = node_info.by_ref[totalNodeRef].type;
  const totalRowName =
    nodeType === 'consolidation-static' || nodeType === 'organisation-static'
      ? nodeStaticIds[staticId as NodeStaticId]
      : node_info.by_ref[totalNodeRef].name;

  return [
    {
      section: REPORT_SECTION.CashFlow,
      account: {
        canonical_category: reportCategory,
        index: totalNodeRef,
        name: totalRowName,
        period_data: totalRowValues,
        type: ROW_KIND.total,
        period_to_date: data.by_ref[totalNodeRef]?.period_to_date || ''
      },
      childCount: node?.children?.length || 0,
      disableLargeSize: true,
      hidePeriodToDateDifference: true,
      isBold: true,
      kind: ROW_KIND.total,
      level: 0,
      name: totalRowName,
      padding,
      isFirst,
      isLast,
      forceRowSize
    }
  ] as RowsType;
};

/**
 * @returns Data structure for Cash flow statement tab
 */
export const getCashFlowStatementConsolidationAccountCategories = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  workspace: WorkspacesListingItem | null,
  expandedRows: (string | number)[],
  consolidatedForecastReport?: ConsolidatedForecastReport | null,
  consolidation?: Consolidation,
  showZeroValueAccounts = false
): RowsType | null => {
  if (!workspace || !consolidatedForecastReport || !consolidation) {
    return null;
  }

  // Starting balance
  const startingBalanceRef = getConsolidationRef(
    consolidatedForecastReport,
    CASH_FLOW_MAIN_CATEGORIES.StartingBalance,
    REPORT_SECTION.CashFlow
  );

  const cashInRef = getConsolidationRef(consolidatedForecastReport, 'cash_in', REPORT_SECTION.CashFlow);
  const cashInNode = cashInRef
    ? findNodeByRef(consolidatedForecastReport.structure[REPORT_SECTION.CashFlow], cashInRef)
    : null;

  const cashOutRef = getConsolidationRef(consolidatedForecastReport, 'cash_out', REPORT_SECTION.CashFlow);
  const cashOutNode = cashOutRef
    ? findNodeByRef(consolidatedForecastReport.structure[REPORT_SECTION.CashFlow], cashOutRef)
    : null;

  const netCashFlowRef = getConsolidationRef(consolidatedForecastReport, 'net_cash_flow', REPORT_SECTION.CashFlow);
  const netCashFlowNode = netCashFlowRef
    ? findNodeByRef(consolidatedForecastReport.structure[REPORT_SECTION.CashFlow], netCashFlowRef)
    : null;

  const closingBalanceRef = getConsolidationRef(
    consolidatedForecastReport,
    CASH_FLOW_MAIN_CATEGORIES.ClosingBalance,
    REPORT_SECTION.CashFlow
  );

  const cashFromBusinessActivitiesRef = getConsolidationRef(
    consolidatedForecastReport,
    'cash_from_business_activities',
    REPORT_SECTION.CashFlow
  );
  const cashFromFinancingInvestingRef = getConsolidationRef(
    consolidatedForecastReport,
    'cash_from_financing_and_investing',
    REPORT_SECTION.CashFlow
  );

  return [
    ...(startingBalanceRef
      ? getConsolidationCashFlowTotals(
          nodeStaticIds,
          startingBalanceRef,
          consolidatedForecastReport,
          CASH_FLOW_MAIN_CATEGORIES.StartingBalance,
          {
            padding: true,
            forceRowSize: SIZES.MEDIUM,
            isFirst: true,
            isLast: true
          }
        )
      : []),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      cashInRef,
      consolidatedForecastReport,
      'cash_in' as CATEGORY_NAME_INDEXES,
      REPORT_SECTION.CashFlow,
      cashInNode,
      showZeroValueAccounts,
      { startingLevel: 1 }
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      cashOutRef,
      consolidatedForecastReport,
      'cash_out' as CATEGORY_NAME_INDEXES,
      REPORT_SECTION.CashFlow,
      cashOutNode,
      showZeroValueAccounts,
      { startingLevel: 1 }
    ),
    ...getConsolidatedCategoryTotalWithAccounts(
      intl,
      nodeStaticIds,
      workspace,
      consolidation,
      expandedRows,
      netCashFlowRef,
      consolidatedForecastReport,
      'net_cash_flow' as CATEGORY_NAME_INDEXES,
      REPORT_SECTION.CashFlow,
      netCashFlowNode,
      showZeroValueAccounts,
      { startingLevel: 1 }
    ),
    ...([
      closingBalanceRef
        ? getConsolidatedCategoryTotal(
            intl,
            nodeStaticIds,
            closingBalanceRef,
            consolidatedForecastReport,
            'closing_balance' as CATEGORY_NAME_INDEXES,
            REPORT_SECTION.CashFlow,
            {
              forceRowSize: SIZES.MEDIUM,
              isFirst: true,
              isLast: true
            },
            true
          )
        : []
    ] as RowType[]),
    ...([
      cashFromBusinessActivitiesRef
        ? getConsolidatedCategoryTotal(
            intl,
            nodeStaticIds,
            cashFromBusinessActivitiesRef,
            consolidatedForecastReport,
            'cash_from_business_activities' as CATEGORY_NAME_INDEXES,
            REPORT_SECTION.CashFlow,
            {
              padding: true,
              forceRowSize: SIZES.MEDIUM,
              isFirst: true
            },
            true
          )
        : []
    ] as RowType[]),
    ...(getProfitAndLossConsolidatedAccountCategories(
      intl,
      nodeStaticIds,
      workspace,
      REPORT_SECTION.CashFlow,
      expandedRows,
      consolidatedForecastReport,
      consolidation,
      true,
      showZeroValueAccounts
    ) || []),
    ...([
      cashFromFinancingInvestingRef
        ? getConsolidatedCategoryTotal(
            intl,
            nodeStaticIds,
            cashFromFinancingInvestingRef,
            consolidatedForecastReport,
            'cash_from_financing_and_investing' as CATEGORY_NAME_INDEXES,
            REPORT_SECTION.CashFlow,
            {
              padding: true,
              forceRowSize: SIZES.MEDIUM,
              isFirst: true
            },
            true
          )
        : []
    ] as RowType[]),
    ...(getBalanceSheetConsolidatedAccountCategories(
      intl,
      nodeStaticIds,
      workspace,
      REPORT_SECTION.CashFlow,
      expandedRows,
      consolidatedForecastReport,
      consolidation,
      true,
      showZeroValueAccounts
    ) || [])
  ];
};

/**
 * @returns All node refs for all report sections (profit & loss, balance sheet and cashflow)
 */
export const getAllNodesRefs = (consolidatedForecastReport: ConsolidatedForecastReport): string[] => {
  const profitLossRefs = collectRefs(consolidatedForecastReport.structure.profit_loss);
  const balanceSheetRefs = collectRefs(consolidatedForecastReport.structure.balance_sheet);
  const cashFlowRefs = collectRefs(consolidatedForecastReport.structure.cash_flow);
  return [...profitLossRefs, ...balanceSheetRefs, ...cashFlowRefs];
};

/**
 * Returns true if row type is 'top-level-static' or 'consolidation-static'
 * @param rowType - The row type to test
 */
export const isTopLevelStaticRowType = (rowType: string) => {
  return rowType === 'consolidation-static' || rowType === 'top-level-static';
};
